import { useCallback, useMemo, useState } from "react";
import { useParams } from "wouter";
import { useClaim } from "@/hooks/use-claim";
import { useAuth } from "@/hooks/use-auth";
import { useClaimActivity } from "@/hooks/use-claim-activity";
import { useComparisons } from "@/hooks/use-comparisons";
import { Loader2, AlertCircle } from "lucide-react";
import { ClaimInfoSidebar } from "@/components/claims/detail/ClaimInfoSidebar";
import { ClaimActivityFeed } from "@/components/claims/detail/ClaimActivityFeed";
import TurboAIChat from "@/components/claims/detail/TurboAIChat";
import { NotesPanel } from "@/components/claims/detail/NotesPanel";
import { EmailPanel } from "@/components/claims/detail/EmailPanel";
import { ClaimDetailTabsList } from "@/components/claims/detail/ClaimDetailTabsList";
import { RightModulesSidebar } from "@/components/claims/detail/RightModulesSidebar";
import type { ExpandedRightModules, RightSidebarModuleKey } from "@/components/claims/detail/RightModulesSidebar";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Sidebar, MobileSidebar } from "@/components/Sidebar";
import { GlobalHeader } from "@/components/GlobalHeader";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";
import { CLAIM_MODULE_VALUES, type ClaimModuleKey } from "@/constants/claim-options";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { PanelLeft, PanelRight } from "lucide-react";

export default function ClaimDetail() {
    const { id } = useParams<{ id: string }>();
    const claimId = useMemo(() => {
        const parsed = Number(id);
        return Number.isFinite(parsed) ? parsed : 0;
    }, [id]);
    const { user } = useAuth();
    const { claim, isLoading, error, updateClaim } = useClaim(claimId);
    const { data: activities = [], isLoading: isActivityLoading } = useClaimActivity(claimId);
    const { comparisons } = useComparisons(claimId);

    const [expandedModules, setExpandedModules] = useState<ExpandedRightModules>({
        comparisons: true,
        documents: false,
        photos: false,
        forms: false,
        links: false,
        tasks: false,
        supplements: false,
        financials: false,
    });
    const [mobileNavOpen, setMobileNavOpen] = useState(false);
    const [mobileInfoOpen, setMobileInfoOpen] = useState(false);
    const [mobileModulesOpen, setMobileModulesOpen] = useState(false);

    const toggleModule = useCallback((module: RightSidebarModuleKey) => {
        setExpandedModules(prev => ({
            ...prev,
            [module]: !prev[module],
        }));
    }, []);

    const handleUpdateClaim = useCallback(
        async (...args: Parameters<typeof updateClaim>) => {
            await updateClaim(...args);
        },
        [updateClaim],
    );

    const canWrite = claim?.canWrite ?? false;

    const hiddenModules = useMemo(() => {
        const assignees = claim?.assignees ?? [];
        const currentAssignee = assignees.find((assignee) => assignee.userId === user?.id);
        return (currentAssignee?.blockedModules ?? []).filter(
            (module): module is ClaimModuleKey => CLAIM_MODULE_VALUES.has(module as ClaimModuleKey),
        );
    }, [claim?.assignees, user?.id]);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-screen" data-testid="loading-spinner">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    if (error || !claim) {
        return (
            <div className="flex items-center justify-center h-screen p-4">
                <Alert variant="destructive" className="max-w-md">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>
                        {error ? error.message : "Claim not found"}
                    </AlertDescription>
                </Alert>
            </div>
        );
    }

    return (
        <div className="flex h-[100dvh] md:h-screen bg-background overflow-hidden">
            <Sidebar />
            <MobileSidebar open={mobileNavOpen} onOpenChange={setMobileNavOpen} />

            <Sheet open={mobileInfoOpen} onOpenChange={setMobileInfoOpen}>
                <SheetContent side="left" className="w-[92vw] max-w-[420px] overflow-hidden p-0 pt-12 lg:hidden">
                    <SheetTitle className="sr-only">Claim Information</SheetTitle>
                    <ClaimInfoSidebar
                        claim={claim}
                        onUpdateClaim={handleUpdateClaim}
                        className="h-full w-full min-w-0 max-w-none"
                    />
                </SheetContent>
            </Sheet>

            <Sheet open={mobileModulesOpen} onOpenChange={setMobileModulesOpen}>
                <SheetContent side="right" className="w-[92vw] max-w-[420px] overflow-hidden p-0 pt-12 lg:hidden">
                    <SheetTitle className="sr-only">Claim Modules</SheetTitle>
                    <RightModulesSidebar
                        claimId={claimId}
                        expandedModules={expandedModules}
                        onToggle={toggleModule}
                        comparisons={comparisons}
                        claim={claim}
                        canWrite={canWrite}
                        hiddenModules={hiddenModules}
                        className="h-full w-full"
                    />
                </SheetContent>
            </Sheet>

            <div className="flex-1 flex flex-col min-w-0 bg-background overflow-hidden animate-in fade-in duration-300">
                <GlobalHeader onOpenMobileNav={() => setMobileNavOpen(true)} />

                {/* Main Content - Three Pane Layout */}
                <div className="flex flex-1 min-h-0">

                    {/* Left Pane: Claim Info Sidebar */}
                    <AppErrorBoundary>
                        <ClaimInfoSidebar
                            claim={claim}
                            onUpdateClaim={handleUpdateClaim}
                            className="hidden lg:flex"
                        />
                    </AppErrorBoundary>

                    {/* Center Pane: Activity Tabs */}
                    <div className="flex-1 flex flex-col min-w-0 overflow-hidden bg-muted/10 lg:border-r">

                        <AppErrorBoundary>
                            <Tabs defaultValue="activity" className="flex flex-col h-full">
                                <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-4 border-b space-y-3">
                                    <div className="flex items-center gap-2 lg:hidden">
                                        <Button variant="outline" size="sm" className="flex-1" onClick={() => setMobileInfoOpen(true)}>
                                            <PanelLeft className="h-4 w-4 mr-2" />
                                            Claim Info
                                        </Button>
                                        <Button variant="outline" size="sm" className="flex-1" onClick={() => setMobileModulesOpen(true)}>
                                            <PanelRight className="h-4 w-4 mr-2" />
                                            Modules
                                        </Button>
                                    </div>
                                    <ClaimDetailTabsList hiddenModules={hiddenModules} />
                                </div>

                                <TabsContent value="activity" className="flex-1 p-0 m-0 min-h-0 relative">
                                    <ClaimActivityFeed
                                        activities={activities}
                                        comparisons={comparisons}
                                        isLoading={isActivityLoading}
                                    />
                                </TabsContent>

                                <TabsContent value="turboai" className="flex-1 p-0 m-0 min-h-0 relative w-full max-w-full overflow-hidden">
                                    <TurboAIChat claimId={claimId} claim={claim} activities={activities} currentUser={user} canWrite={claim.canWrite ?? false} />
                                </TabsContent>

                                {!hiddenModules.includes('notes') && (
                                    <TabsContent value="notes" className="flex-1 p-0 m-0 min-h-0 relative">
                                        <NotesPanel claimId={claimId} canWrite={canWrite} />
                                    </TabsContent>
                                )}

                                {!hiddenModules.includes('emails') && (
                                    <TabsContent value="email" className="flex-1 p-0 m-0 min-h-0 relative">
                                        <EmailPanel claimId={claimId} canWrite={canWrite} claim={claim} />
                                    </TabsContent>
                                )}
                            </Tabs>
                        </AppErrorBoundary>
                    </div>

                    {/* Right Pane: Modules Sidebar */}
                    <AppErrorBoundary>
                        <RightModulesSidebar
                            claimId={claimId}
                            expandedModules={expandedModules}
                            onToggle={toggleModule}
                            comparisons={comparisons}
                            claim={claim}
                            canWrite={canWrite}
                            hiddenModules={hiddenModules}
                            className="hidden lg:flex"
                        />
                    </AppErrorBoundary>

                </div>
            </div>
        </div>
    );
}
